import { create } from "zustand";
import {
  IFormSamplesInputs,
  SampleRegistrationTypes,
  SampleStateProcessingType,
  SampleStateType,
  TableProcessingMapeoTypes,
} from "../../../types/samples.types";
import { filter, syncData } from "./helpers";
import {
  deleteData,
  getData,
  postData,
  putData,
  postDataFile,
  getDataFile,
  postDataFileDat,
} from "../../../../lib/utils/formHelpers";
import { errorAlert, successAlert } from "../../../utils/alerts";
import { mapObjectToIds } from "../../../utils/helpers";
import { MapKeys, mapsNameInitial } from "./constants";
import useAnalysis from "./useAnalysis";
import { UserType } from "../../../types/genericTables.types";
import { mapsProcessingCategory } from "@app/utils/tables/processing.tables";
import { KeeperFormValues } from "../components/modals/KeeperModal";
import dayjs from "dayjs";
import { GridSortDirection } from "@mui/x-data-grid";

type UseSampleType = {
  page: 1;
  limit: 10;
  totalItems: 0;
  setPage: (page) => void;
  setLimit: (limit) => void;
  orderBy: string;
  order: GridSortDirection;
  setOrderBy: (field: string) => void;
  setOrder: (sort: GridSortDirection) => void;
  stateSample: {
    sampling: SampleStateType;
    management: SampleStateType;
    conditioning: SampleStateType;
    processing: SampleStateProcessingType;
  };
  samplesList: SampleRegistrationTypes[];
  clientReferenceList: string[];
  maslReferenceList: string[];
  get: (
    id: SampleRegistrationTypes["id"]
  ) => SampleRegistrationTypes | undefined;
  query: IFormSamplesInputs;
  setQuery: (query: IFormSamplesInputs) => void;
  fetchAll: () => void;
  fetchOne: (id: SampleRegistrationTypes["id"]) => Promise<SampleRegistrationTypes | undefined>;
  fetchAllIds: () => Promise<number[]>;
  loading: boolean;
  loadingSave: boolean;
  loadingExport: boolean;
  remove: (id: SampleRegistrationTypes["id"]) => Promise<void>;
  filteredSample: () => SampleRegistrationTypes[];
  save: (sample: Partial<any>, user: UserType) => Promise<boolean>;
  saveInitial: (sampling: Partial<any>, type: MapKeys) => Promise<boolean>;
  saveProcessingInitial: (
    proccesing: Partial<any>,
    type: MapKeys,
    processingMapDataTable: TableProcessingMapeoTypes
  ) => Promise<boolean>;
  getAndSet: (
    id: SampleRegistrationTypes["id"]
  ) => Promise<SampleRegistrationTypes | undefined>;
  deleteProcessing: (ids: number[]) => Promise<void>;
  exportPlan: (query: IFormSamplesInputs) => Promise<boolean>;
  updateProcessing: (idSampleRegistration: number) => Promise<void>;
  exportKeeper: (query: KeeperFormValues) => Promise<boolean>;
  importKeeper: (file: File) => Promise<boolean>;
  getCurrentProcessing: () => SampleStateProcessingType;
};

export const useSample = create<UseSampleType>((set, get) => ({
  page: 1,
  limit: 10,
  totalItems: 0,
  setPage: (page) => set({ page }),
  setLimit: (limit) => set({ limit }),
  orderBy: "",
  order: undefined,
  setOrderBy: (orderBy) => set({ orderBy }),
  setOrder: (order) => set({ order }),
  stateSample: {
    sampling: { isSave: false, id: undefined, current: undefined, ids: [] },
    management: { isSave: false, id: undefined, current: undefined, ids: [] },
    conditioning: { isSave: false, id: undefined, current: undefined, ids: [] },
    processing: { isSave: false, id: undefined, current: undefined, ids: [] },
  },
  getCurrentProcessing: () => get().stateSample.processing,
  loading: false,
  loadingSave: false,
  loadingExport: false,
  samplesList: [],
  clientReferenceList: [],
  maslReferenceList: [],
  query: {},
  filteredSample: () => filter(get().query, get().samplesList),
  setQuery: (query) => set({ query }),
  get: (id) => get().samplesList.find((sample) => sample.id === id),
  fetchAll: async () => {
    const { query, page, limit, orderBy, order } = get();
    set({ loading: true });
    try {
      const params = new URLSearchParams({
        ...Object.entries(query).reduce(
          (acc, [k, v]) => ({
            ...acc,
            [k]:
              v != null
                ? typeof v === "object"
                  ? JSON.stringify(v)
                  : String(v)
                : "",
          }),
          {}
        ),
        page: page.toString(),
        limit: limit.toString(),
      });
      if (orderBy && order) {
        params.append("order_by", orderBy);
        params.append("order", order);
      }

      const endpoint = `/sample-registration?${params}`;
      const { data: response } = await getData(endpoint);

      if (response) {
        set({
          samplesList: response.data,
          clientReferenceList: Array.from(
            new Set(
              response.data.map(
                (sample: SampleRegistrationTypes) => sample.client_reference
              )
            )
          ),
          maslReferenceList: Array.from(
            new Set(
              response.data.map(
                (sample: SampleRegistrationTypes) => sample.masl_reference
              )
            )
          ),
          totalItems: response.meta.total_items,
        });
      }
    } catch (error) {
      console.error("Error fetching samples:", error);
    } finally {
      set({ loading: false });
    }
  },

  fetchOne: async (id: SampleRegistrationTypes["id"]) => {
    set({ loading: true });
    try {
      const endpoint = `/sample-registration/${id}`;
      const { data: response } = await getData(endpoint);

      if (response) {
        const updatedSample = response as SampleRegistrationTypes;
        
        // Actualizar el sample en la lista existente
        set((state) => ({
          samplesList: state.samplesList.map((sample) =>
            sample.id === id ? updatedSample : sample
          ),
        }));
        
        return updatedSample;
      }
      return undefined;
    } catch (error) {
      console.error("Error fetching sample:", error);
      return undefined;
    } finally {
      set({ loading: false });
    }
  },

  fetchAllIds: async () => {
    const { query, orderBy, order } = get();
    try {
      const params = new URLSearchParams({
        ...Object.entries(query).reduce(
          (acc, [k, v]) => ({
            ...acc,
            [k]:
              v != null
                ? typeof v === "object"
                  ? JSON.stringify(v)
                  : String(v)
                : "",
          }),
          {}
        ),
        ids_only: "true",
      });
      if (orderBy && order) {
        params.append("order_by", orderBy);
        params.append("order", order);
      }

      const endpoint = `/sample-registration/all-ids?${params}`;
      const { data: response } = await getData(endpoint);

      if (response && Array.isArray(response)) {
        return response;
      }
      return [];
    } catch (error) {
      console.error("Error fetching all sample IDs:", error);
      return [];
    }
  },

  remove: async (id: SampleRegistrationTypes["id"]) => {
    try {
      set({ loading: true });
      const endpoint = `/sample-registration/${id}`;
      const resp = await deleteData(endpoint);
      if (resp.success) successAlert("Elemento eliminado correctamente");
      else errorAlert("Error al eliminar el elemento");
    } catch (error) {
      errorAlert("Error al eliminar el elemento");
    } finally {
      set({ loading: false });
      get().fetchAll();
    }
  },
  save: async (sample: Partial<any>, user: UserType) => {
    set({ loadingSave: true });

    const mapeaPayload = mapObjectToIds(sample);

    try {
      const isNew = !mapeaPayload?.id;
      const endpoint = `/sample-registration/${
        isNew ? "create" : mapeaPayload.id + "/edit"
      }`;

      const { masl_reference, ...rest } = mapeaPayload;

      const response = isNew
        ? await postData(endpoint, rest)
        : await putData(endpoint, rest);

      if (response.success) {
        if (isNew) {
          if (sample?.comments_external) {
            const endpointComments = "/comments/create";

            const payloadComments = {
              analyst: user.id,
              comment: sample.comments_external,
              date_time: new Date().toISOString(),
              internal: 0,
              sample_registration: response.data.id,
            };
            await postData(endpointComments, payloadComments);
          }
          if (sample?.comments_internal) {
            const endpointComments = "/comments/create";

            const payloadComments = {
              analyst: user.id,
              comment: sample.comments_internal,
              date_time: new Date().toISOString(),
              internal: 1,
              sample_registration: response.data.id,
            };
            await postData(endpointComments, payloadComments);
          }
        }
        successAlert("Elemento guardado correctamente");
        get().fetchAll();
        return true;
      } else {
        errorAlert("Error al guardar el elemento");
        return false;
      }
    } catch (error) {
      errorAlert("Error al guardar el elemento");
      return false;
    } finally {
      set({ loadingSave: false });
    }
  },

  saveProcessingInitial: async (
    payloads: Partial<any> | Partial<any>[],
    type: MapKeys,
    processingMapDataTable: TableProcessingMapeoTypes
  ) => {
    set({ loadingSave: true });
    const isArray = Array.isArray(payloads);
    const payloadArray = isArray ? payloads : [payloads];
    const currentProcessing = get().stateSample.processing.current;

    try {
      for (const proccesing of payloadArray) {
        const processing_capsule_weights: any[] = [];
        const responseIds: number[] = [];
        const responseCurrent: any[] = [];
        const isCalcination =
          proccesing?.processing_category ===
          mapsProcessingCategory["calcination"];
        const isDrying =
          proccesing?.processing_category === mapsProcessingCategory["drying"];
        const hasRows = isCalcination || isDrying;

        const id = proccesing?.id;
        const isNew = !id;
        const endpoint = `/${type}/${isNew ? "create" : id + "/edit"}`;
        const response = isNew
          ? await postData(endpoint, proccesing)
          : await putData(endpoint, proccesing);

        responseIds.push(response.data.id);

        if (hasRows) {
          const dataRowProcessing = isCalcination
            ? processingMapDataTable["calcinacion"]
            : processingMapDataTable["drying"];

          const preloadRows = currentProcessing?.find(
            (p: any) =>
              p.processing_category?.id ==
              (isCalcination
                ? mapsProcessingCategory["calcination"]
                : mapsProcessingCategory["drying"])
          )?.processing_capsule_weights;

          const capsuleNumber = proccesing.capsules_number;

          const { updatedData, idsToDelete } = syncData(
            preloadRows || [],
            dataRowProcessing,
            capsuleNumber
          );

          if (idsToDelete.length > 0) {
            for (const id of idsToDelete) {
              const endpointDeleteRows = `/processing-capsule-weight/${id}`;
              await deleteData(endpointDeleteRows);
            }
          }

          for (const processingCapsuleWeight of updatedData) {
            const id = processingCapsuleWeight?.id;
            const isNew = !id;
            const endpointRows = `/processing-capsule-weight/${
              isNew ? "create" : id + "/edit"
            }`;

            const payloadRows = {
              processing: response.data.id,
              ...processingCapsuleWeight,
            };

            const responseRows = isNew
              ? await postData(endpointRows, payloadRows)
              : await putData(endpointRows, payloadRows);
            processing_capsule_weights.push(responseRows.data);
          }
        }

        responseCurrent.push({ ...response.data, processing_capsule_weights });

        if (!response.success) {
          errorAlert("Error al guardar el elemento");
        }
      }

      return true;
    } catch (error) {
      errorAlert("Error al guardar los elementos");
      return false;
    }
  },

  updateProcessing: async (idSampleRegistration) => {
    try {
      const endpointSAmple = `/sample-registration/${idSampleRegistration}`;
      const { data: sample } = await getData(endpointSAmple);

      const convertSampleProcessing = sample?.processings;
      const idsProcessing =
        convertSampleProcessing && convertSampleProcessing.length > 0
          ? convertSampleProcessing.map((p: any) => p.id)
          : [];

      set({
        stateSample: {
          ...get().stateSample,
          processing: {
            isSave: !!idsProcessing.length,
            id: Math.max(...idsProcessing),
            ids: idsProcessing,
            current:
              idsProcessing.length > 0
                ? await Promise.all(
                    idsProcessing.map(async (id) => {
                      try {
                        const response = await getData(`/processing/${id}`);
                        return response.data;
                      } catch (error) {
                        console.error(
                          `Error fetching data for ID ${id}:`,
                          error
                        );
                        return null;
                      }
                    })
                  ).then((results) => results.filter((item) => item !== null))
                : undefined,
          },
        },
      });
    } catch (error) {
      console.error("Error fetching samples:", error);
    } finally {
      get().fetchAll();
      set({ loadingSave: false });
    }
  },

  saveInitial: async (sampling: Partial<any>, type: MapKeys) => {
    set({ loadingSave: true });

    const keyInicialState = mapsNameInitial[type];

    try {
      const id = get().stateSample[keyInicialState]?.id;
      const isNew = !id;
      const endpoint = `/${type}/${isNew ? "create" : id + "/edit"}`;
      const response = isNew
        ? await postData(endpoint, sampling)
        : await putData(endpoint, sampling);
      if (response.success) {
        successAlert("Elemento guardado correctamente");
        get().fetchAll();
        set({
          stateSample: {
            ...get().stateSample,
            [keyInicialState]: {
              isSave: true,
              id: response.data.id,
              current: response.data,
            },
          },
        });

        return true;
      } else {
        errorAlert("Error al guardar el elemento");
        return false;
      }
    } catch (error) {
      errorAlert("Error al guardar el elemento");
      return false;
    } finally {
      set({ loadingSave: false });
    }
  },
  getAndSet: async (id: SampleRegistrationTypes["id"]) => {
    const sample = get().get(id);
    if (!sample) return undefined;

    const idSampling = sample?.sampling?.id;
    const idManagement = sample?.sampleManagement?.id;
    const idConditioning = sample?.conditioning?.id;

    const convertSampleProcessing = sample?.processings;
    const idsProcessing =
      convertSampleProcessing && convertSampleProcessing.length > 0
        ? convertSampleProcessing.map((p: any) => p.id)
        : [];

    if (sample?.preparations?.length > 0) {
      await useAnalysis.getState().fetchPreparations(sample?.preparations);
    }

    set({
      stateSample: {
        sampling: {
          isSave: !!idSampling,
          id: idSampling,
          current: idSampling
            ? await getData(`/sampling/${idSampling}`).then((res) => res.data)
            : undefined,
          ids: [],
        },
        management: {
          isSave: !!idManagement,
          id: idManagement,
          current: idManagement
            ? await getData(`/sample-management/${idManagement}`).then(
                (res) => res.data
              )
            : undefined,
          ids: [],
        },
        conditioning: {
          isSave: !!idConditioning,
          id: idConditioning,
          current: idConditioning
            ? await getData(`/conditioning/${idConditioning}`).then(
                (res) => res.data
              )
            : undefined,
          ids: [],
        },
        processing: {
          isSave: !!idsProcessing.length,
          id: Math.max(...idsProcessing),
          ids: idsProcessing,
          current:
            idsProcessing.length > 0
              ? await Promise.all(
                  idsProcessing.map(async (id) => {
                    try {
                      const response = await getData(`/processing/${id}`);
                      return response.data;
                    } catch (error) {
                      console.error(`Error fetching data for ID ${id}:`, error);
                      return null;
                    }
                  })
                ).then((results) => results.filter((item) => item !== null))
              : undefined,
        },
      },
    });

    return sample;
  },

  deleteProcessing: async (idsToDelete: number[]) => {
    if (idsToDelete.length === 0) return;
    try {
      set({ loading: true });
      await Promise.all(
        idsToDelete.map(async (id) => {
          const endpoint = `/processing/${id}`;
          id && (await deleteData(endpoint));
        })
      );

      set((state) => {
        const keyInicialState = mapsNameInitial["processing"];
        const currentState = state.stateSample[keyInicialState];

        return {
          stateSample: {
            ...state.stateSample,
            [keyInicialState]: {
              ...currentState,
              ids: (currentState.ids || []).filter(
                (id: number) => !idsToDelete.includes(id)
              ),
              current: (currentState.current || []).filter(
                (item: { id: number }) => !idsToDelete.includes(item.id)
              ),
            },
          },
        };
      });
    } catch (error) {
      errorAlert("Error al eliminar el elemento");
    } finally {
      set({ loading: false });
      get().fetchAll();
    }
  },
  exportPlan: async (query: IFormSamplesInputs) => {
    set({ loadingExport: true });
    const endpoint = "/sample-registration/exportPlan";

    try {
      const params = new URLSearchParams();

      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (typeof value === "object") {
            params.append(key, String(value.id));
          } else {
            params.append(key, String(value));
          }
        }
      });

      // Construir la URL completa con los parámetros
      const fullUrl = `${endpoint}?${params.toString()}`;

      const { data: response } = await postDataFile(fullUrl, {
        responseType: "blob",
      });

      if (!response) {
        return false;
      }

      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `planMuestreo.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      successAlert("Informe descargado correctamente");

      return true;
    } catch (error) {
      errorAlert("Error al descargar el informe");
      return false;
    } finally {
      set({ loadingExport: false });
    }
  },
  exportKeeper: async (query: KeeperFormValues) => {
    set({ loadingExport: true });
    const endpoint = "/sample-registration/exportKeeper";

    const generateParams = (query: any) => {
      const params = new URLSearchParams();

      if (query.installation?.id !== undefined) {
        params.append("installation", query.installation.id.toString());
      }

      if (query.reason?.id !== undefined) {
        params.append("reason", query.reason.id.toString());
      }

      if (query.filterReport?.includes("check_referent_app_info")) {
        params.append("check_referent_app_info", "1");
      } else {
        params.append("check_referent_app_info", "0");
      }

      if (query.filterReport?.includes("check_referent_cliente")) {
        params.append("check_referent_cliente", "1");
      } else {
        params.append("check_referent_cliente", "0");
      }

      if (query.filterReport?.includes("check_solid")) {
        params.append("check_solid", "1");
      } else {
        params.append("check_solid", "0");
      }

      if (query.year_pickup !== undefined) {
        params.append("year", query.year_pickup.toString());
      }

      if (query.start_week_pickup !== undefined) {
        params.append("start_week", query.start_week_pickup.toString());
      }

      if (query.end_week_pickup !== undefined) {
        params.append("end_week", query.end_week_pickup.toString());
      }

      if (query.evo_start_date !== undefined) {
        params.append(
          "date_from",
          dayjs(query?.evo_start_date).format("YYYY-MM-DD")
        );
      }

      if (query.evo_end_date !== undefined) {
        params.append(
          "date_to",
          dayjs(query?.evo_end_date).format("YYYY-MM-DD")
        );
      }
      if (query.motivo_muestreo !== undefined) {
        params.append("reason", query.motivo_muestreo.id.toString());
      }

      return params;
    };

    const params = generateParams(query);

    try {
      const fullUrl = `${endpoint}?${params.toString()}`;

      const { data: response } = await getDataFile(fullUrl, {
        responseType: "blob",
      });

      if (!response) {
        return false;
      }

      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `keeper.dat`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      successAlert("Informe descargado correctamente");

      return true;
    } catch (error) {
      errorAlert("Error al descargar el informe");
      return false;
    } finally {
      set({ loadingExport: false });
    }
  },
  importKeeper: async (file: File) => {
    set({ loadingExport: true });

    try {
      const formData = new FormData();
      formData.append("file", file);

      const endpoint = "/sample-registration/importKeeper";
      const response = await postDataFileDat(endpoint, formData);

      if (response.success) {
        successAlert("Archivo importado correctamente");
        return true;
      } else {
        errorAlert("Error al importar el archivo");
        return false;
      }
    } catch (error) {
      errorAlert("Error al importar el archivo");
      return false;
    } finally {
      set({ loadingExport: false });
    }
  },
}));
